import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Loader2 } from "lucide-react";
import { buildSeo } from "@/lib/seo";
import { Eyebrow, MarketingLayout } from "@/components/marketing/Layout";
import { useAutoTranslate } from "@/components/AutoTranslate";

// Blog index: the articles existed but had no hub page — they were orphan
// URLs reachable only through the sitemap. This page gives them internal
// links (SEO) and gives visitors a way to actually find them.

const POSTS = [
  {
    slug: "what-is-an-ai-twin",
    title: "What Is an AI Twin? The Complete 2026 Guide",
    description:
      "The full definition of an AI Twin, how it differs from a chatbot and an industrial digital twin, what it does, and how to build one from your website.",
    published: "2026-07-20",
    tag: "Guide",
  },
  {
    slug: "best-ai-chatbot-alternatives-b2b",
    title: "The 7 Best AI Chatbot Alternatives for B2B (2026)",
    description:
      "Chatbase, Botpress, Intercom, Drift and more — compared for B2B teams that care about qualified pipeline, not chat deflection. How to pick in one afternoon.",
    published: "2026-07-21",
    tag: "Comparison",
  },
  {
    slug: "ai-customer-support-automation",
    title: "AI Customer Support Automation Without the Robotic Feeling",
    description:
      "How to automate the repetitive 60–80% of support, ground every answer in your real docs, and get the human handoff right — with metrics that actually matter.",
    published: "2026-07-22",
    tag: "Guide",
  },
  {
    slug: "convert-website-visitors-with-ai",
    title: "How to Convert More Website Visitors With AI",
    description:
      "97% of traffic leaves without a word. The playbook for turning read-only pages into grounded conversations that qualify and capture leads in the moment.",
    published: "2026-07-23",
    tag: "Playbook",
  },
  {
    slug: "ai-twins-for-agencies",
    title: "AI Twins for Agencies: A New Service Line",
    description:
      "Deploy an AI Twin for your own inbound, then productize it as a high-margin recurring service for every client. The playbook for both moves.",
    published: "2026-07-24",
    tag: "Industry",
  },
  {
    slug: "botpress-alternatives-b2b",
    title: "Botpress Alternatives for B2B: The Revenue-First Guide (2026)",
    description:
      "Comparing the best Botpress alternatives for B2B teams that need revenue-focused AI Twins — not another workflow builder. Twin-to-Twin discovery, 60-second setup, Swiss company.",
    published: "2026-07-25",
    tag: "Comparison",
  },
  {
    slug: "ai-lead-qualification",
    title: "AI Lead Qualification for B2B: A 2026 Playbook",
    description:
      "How AI Twins automate B2B lead scoring and qualification — grounded on your knowledge and synced to your CRM, without brittle intent trees.",
    published: "2026-07-06",
    tag: "Playbook",
  },
  {
    slug: "ai-twin-vs-chatbot",
    title: "AI Twin vs. Chatbot: Why Static Bots Lose in 2026",
    description:
      "Compare AI Digital Twins to traditional AI chatbot platforms: automated knowledge ingestion, reasoning, and Twin-to-Twin discovery vs. static bots.",
    published: "2026-07-05",
    tag: "Comparison",
  },
] as const;

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export const Route = createFileRoute("/blog/")({
  head: () => {
    const seo = buildSeo({
      path: "/blog",
      title: "Blog — AI Twins & lead qualification | Reality Twin",
      description:
        "Guides and comparisons on AI Twins for business: lead qualification playbooks, chatbot platform comparisons and updates from the Reality Twin network.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Reality Twin Blog",
            url: "https://realitytwin.io/blog",
            publisher: {
              "@type": "Organization",
              name: "Reality Twin",
              url: "https://realitytwin.io",
            },
            blogPost: POSTS.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              description: p.description,
              datePublished: p.published,
              url: `https://realitytwin.io/blog/${p.slug}`,
              author: { "@type": "Person", name: "Ajdin Ruznic" },
            })),
          }),
        },
      ],
    };
  },
  component: BlogIndexPage,
});

function BlogIndexPage() {
  const CHROME = [
    "Blog",
    "Notes from the twin network.",
    "Guides, comparisons and playbooks on AI Twins for business — written from what we learn building Reality Twin.",
    "Read article",
    "More every week. Want a topic covered? Write to",
  ];
  const strings = [...CHROME, ...POSTS.flatMap((p) => [p.tag, p.title, p.description])];
  const { out, busy } = useAutoTranslate(strings);
  let cur = 0;
  const C = CHROME.map(() => out[cur++] ?? "");
  const postsT = POSTS.map(() => ({ tag: out[cur++], title: out[cur++], description: out[cur++] }));

  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <Eyebrow>{C[0]}</Eyebrow>
          <h1 className="mt-4 font-serif text-[48px] sm:text-[72px] leading-[0.98] tracking-[-0.035em] max-w-3xl">
            {C[1]}
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-[1.55] text-muted-foreground flex items-center gap-2">
            {C[2]}
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
            {POSTS.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-card p-7 flex flex-col hover:bg-cream transition"
              >
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                  <span className="font-mono">{postsT[i]?.tag ?? p.tag}</span>
                  <time dateTime={p.published}>{formatDate(p.published)}</time>
                </div>
                <h2 className="mt-5 font-serif text-[24px] leading-tight tracking-[-0.02em] flex-1">
                  {postsT[i]?.title ?? p.title}
                </h2>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
                  {postsT[i]?.description ?? p.description}
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground">
                  {C[3]}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-10 text-[13px] text-muted-foreground">
            {C[4]}{" "}
            <a
              href="mailto:hello@realitytwin.io"
              className="underline hover:text-foreground transition"
            >
              hello@realitytwin.io
            </a>
            .
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
